"use client";

import type { FormEvent } from "react";
import { useState } from "react";
import { apiBaseUrl } from "../../lib/api";

type FormState = {
  name: string;
  email: string;
  company: string;
  interestArea: string;
  message: string;
  website: string;
  startedAt: string;
};

function createInitialState(): FormState {
  return {
    name: "",
    email: "",
    company: "",
    interestArea: "cloud-design",
    message: "",
    website: "",
    startedAt: String(Date.now())
  };
}

const initialState: FormState = createInitialState();

export function ContactForm() {
  const [form, setForm] = useState<FormState>(initialState);
  const [status, setStatus] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setIsSubmitting(true);
    setStatus("");

    try {
      const response = await fetch(`${apiBaseUrl()}/api/v1/leads/discovery`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(form)
      });

      if (!response.ok) {
        throw new Error(`Submission failed with status ${response.status}`);
      }

      setForm(createInitialState());
      setStatus("Thanks. Your discovery request was received.");
    } catch {
      setStatus("Unable to submit right now. Please try again shortly.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form className="contactForm" onSubmit={onSubmit}>
      <label htmlFor="name">Name</label>
      <input
        id="name"
        required
        value={form.name}
        onChange={(event) => setForm({ ...form, name: event.target.value })}
      />

      <label htmlFor="email">Work Email</label>
      <input
        id="email"
        type="email"
        required
        value={form.email}
        onChange={(event) => setForm({ ...form, email: event.target.value })}
      />

      <label htmlFor="company">Company</label>
      <input
        id="company"
        required
        value={form.company}
        onChange={(event) => setForm({ ...form, company: event.target.value })}
      />

      <label htmlFor="interestArea">Primary Interest</label>
      <select
        id="interestArea"
        value={form.interestArea}
        onChange={(event) =>
          setForm({ ...form, interestArea: event.target.value })
        }
      >
        <option value="cloud-design">Cloud design and architecture</option>
        <option value="migration">Migration program</option>
        <option value="managed-services">Managed services</option>
        <option value="ai-infrastructure">AI infrastructure</option>
      </select>

      <label htmlFor="message">Project Overview</label>
      <textarea
        id="message"
        required
        rows={6}
        value={form.message}
        onChange={(event) => setForm({ ...form, message: event.target.value })}
      />

      <input
        tabIndex={-1}
        autoComplete="off"
        className="contactTrap"
        aria-hidden="true"
        value={form.website}
        onChange={(event) => setForm({ ...form, website: event.target.value })}
      />

      <input type="hidden" value={form.startedAt} readOnly />

      <button className="cta" type="submit" disabled={isSubmitting}>
        {isSubmitting ? "Submitting..." : "Submit Discovery Request"}
      </button>

      {status ? <p className="status">{status}</p> : null}
    </form>
  );
}
